import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  profilesService,
  type ProfileUpdate,
} from "@/services/supabase/profiles";
import { useToast } from "@/hooks/use-toast";

export function useProfile(idOrSlug?: string) {
  const result = useQuery({
    queryKey: ["profile", idOrSlug],
    queryFn: () => profilesService.getProfile(idOrSlug!),
    enabled: !!idOrSlug,
    staleTime: Infinity, // Never consider data stale - prevent unnecessary refetches
    refetchOnMount: false, // Don't refetch on mount if data exists
    refetchOnWindowFocus: false,
  });


  return result;
}

export function useProfiles(filters = {}, pagination = {}) {
  return useQuery({
    queryKey: ["profiles", filters, pagination],
    queryFn: () => profilesService.getProfiles(filters, pagination),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: ProfileUpdate }) =>
      profilesService.updateProfile(id, updates),
    onSuccess: (data, variables) => {
      // Update the specific profile cache
      queryClient.setQueryData(["profile", variables.id], data);

      // Invalidate profiles list to ensure consistency
      queryClient.invalidateQueries(["profiles"]);

      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteProfile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: profilesService.deleteProfile,
    onSuccess: (_, profileId) => {
      // Remove from cache
      queryClient.removeQueries(["profile", profileId]);
      queryClient.invalidateQueries(["profiles"]);

      toast({
        title: "Profile deleted",
        description: "Profile has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting profile",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useProfileLike() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      profileId,
      userId,
      isLiked,
    }: {
      profileId: string;
      userId: string;
      isLiked: boolean;
    }) => profilesService.toggleLike(profileId, userId, isLiked),
    onMutate: async ({ profileId, isLiked }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries(["profile", profileId]);

      // Snapshot previous value
      const previousProfile = queryClient.getQueryData(["profile", profileId]);

      // Optimistically update
      queryClient.setQueryData(["profile", profileId], (old: any) => {
        if (!old) return old;
        const likeDelta = isLiked ? 1 : -1;
        return {
          ...old,
          like_count: Math.max(0, (old.like_count || 0) + likeDelta),
          is_liked: isLiked,
        };
      });

      return { previousProfile };
    },
    onError: (err, variables, context) => {
      // Revert optimistic update
      if (context?.previousProfile) {
        queryClient.setQueryData(
          ["profile", variables.profileId],
          context.previousProfile
        );
      }

      toast({
        title: "Error",
        description: "Failed to update like status",
        variant: "destructive",
      });
    },
    onSettled: (_, __, variables) => {
      // Always refetch to ensure consistency
      queryClient.invalidateQueries(["profile", variables.profileId]);
    },
  });
}

export function useProfileViews() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: profilesService.incrementViewCount,
    onSuccess: (_, profileId) => {
      // Optimistically increment view count
      queryClient.setQueryData(["profile", profileId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          view_count: (old.view_count || 0) + 1,
        };
      });
    },
  });
}

export function useRelatedProfiles(profileId?: string) {
  return useQuery({
    queryKey: ["relatedProfiles", profileId],
    queryFn: () => profilesService.getRelatedProfiles(profileId!),
    enabled: !!profileId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}
